import { addYears, subYears } from 'date-fns';
import { snakeCase } from 'lodash/fp';

import { CombinedStepFieldKeyMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor/utils';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  stepActionTargets,
  stepsWorkflowVersionTargets,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  getAllWorkflowConditionalSteps,
  workflowVtmplvIds,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import { getConditionalRuleParts, RuleValueTypes } from 'dashboard/lib/workflows';
import { CheckName } from 'dashboard/models/check';
import {
  CombinedStepConfig,
  InquiryTemplateVersionStep,
} from 'dashboard/models/inquiry-template-version';
import {
  StubsWithOptionalId,
  VERIFICATION_TYPE_TO_PRESETS,
} from 'dashboard/models/simulate/preset';
import {
  SandboxForcedStatus,
  VerificationTemplateVersionStubs,
} from 'dashboard/models/simulate/stubs';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import {
  isCreateVerificationActionWithFieldMapping,
  WorkflowConditional,
  WorkflowElse,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import { FieldSchema, FieldSchemaType, FieldValue } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import {
  isComponentFieldConfigKey,
  isUiFormFieldComponentConfig,
  isUiFormMultiFieldComponentConfig,
  UiComponentConfig,
} from 'lib/interfaces/ui-components';

export const generateSampleDLExtractionsStubs = (date: Date) => ({
  birthdate: '1977-07-17',
  'identification-number': 'I1234562',
  'id-class': 'dl',
  'name-first': 'ALEXANDER J',
  'name-last': 'SAMPLE',
  'address-street-1': '600 CALIFORNIA STREET',
  'address-city': 'SAN FRANCISCO',
  'address-subdivision': 'CA',
  'address-postal-code': '94109',
  'issuing-authority': 'CA',
  'issuing-subdivision': 'CA',
  'country-code': 'US',
  sex: 'Male',
  'issue-date': subYears(date, 1).toISOString().substring(0, 10),
  'expiration-date': addYears(date, 5).toISOString().substring(0, 10),
});

export const CHECK_STUBS_SUPPORTED_VERIFICATION_MODELS = [
  ApiModel.VerificationTemplateVersionGovernmentId,
  ApiModel.VerificationTemplateVersionSelfie,
  ApiModel.VerificationTemplateVersionDatabase,
  ApiModel.VerificationTemplateVersionDocument,
  ApiModel.VerificationTemplateVersionDatabaseTin,
  ApiModel.VerificationTemplateVersionDatabaseStandard,
  ApiModel.VerificationTemplateVersionDatabaseEcbsv,
  ApiModel.VerificationTemplateVersionAamva,
  ApiModel.VerificationTemplateVersionDatabaseBusiness,
];

export const MATCH_REQUIREMENT_CHECK_NAMES = [
  CheckName.DatabaseIdentityComparison,
  CheckName.DatabaseBusinessIdentityComparison,
];

export const initialVerificationTemplateVersionStubs = (
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>,
  date: Date,
): VerificationTemplateVersionStubs[] => {
  return Object.values(verificationTemplateVersions).map((verificationTemplateVersion) => {
    if (CHECK_STUBS_SUPPORTED_VERIFICATION_MODELS.includes(verificationTemplateVersion.type)) {
      if (verificationTemplateVersion.type === ApiModel.VerificationTemplateVersionGovernmentId) {
        return {
          id: verificationTemplateVersion.id,
          'verification-stubs': {
            'check-stubs': [],
          },
          'document-stubs': {
            kind: 'government_id',
            extractions: generateSampleDLExtractionsStubs(date),
          },
        };
      } else {
        return {
          id: verificationTemplateVersion.id,
          'verification-stubs': {
            'check-stubs': [],
          },
        };
      }
    }
    return {
      id: verificationTemplateVersion.id,
      'verification-stubs': {
        'status-stub': SandboxForcedStatus.Passed,
      },
    };
  });
};

export const allVerificationsWithStatus = (
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>,
  status: SandboxForcedStatus,
): VerificationTemplateVersionStubs[] => {
  return Object.values(verificationTemplateVersions).map((verificationTemplateVersion) => {
    return {
      id: verificationTemplateVersion.id,
      'verification-stubs': {
        'status-stub': status,
      },
    };
  });
};

/**
 * Maps the given verificationTemplateVersions to the initial stubs and presets for an inquiry template simulation
 * @returns Mapping of field name (from the formInquiryTemplateSimulate form) to stubs
 */
export const initialSimulateStubsAndPresets = (
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>,
  options: {
    isAdvancedMode: boolean;
  },
): {
  'verification-template-version-stubs': VerificationTemplateVersionStubs[];
  'fields-stubs': Record<string, FieldValue>;
  'vtv-to-selected-preset': Record<string, StubsWithOptionalId>;
} => {
  const { isAdvancedMode } = options;

  const initialAdvancedStubs = initialVerificationTemplateVersionStubs(
    verificationTemplateVersions,
    new Date(),
  );

  let vtvStubs: VerificationTemplateVersionStubs[] = [];
  const fieldsStubs = {};
  const vtvToPreset: Record<string, StubsWithOptionalId> = {};

  if (isAdvancedMode) {
    Object.values(verificationTemplateVersions).forEach((vtv) => {
      const preset = (VERIFICATION_TYPE_TO_PRESETS[vtv.type] || [])?.[0];
      if (preset) {
        vtvToPreset[vtv.id] = preset;
        vtvStubs.push({
          id: vtv.id,
          ...preset.stubs,
        });
      } else {
        const initialStub = initialAdvancedStubs.find((stubs) => stubs.id === vtv.id);
        if (initialStub) {
          vtvStubs.push(initialStub);
        }
      }
    });
  } else {
    vtvStubs = allVerificationsWithStatus(verificationTemplateVersions, SandboxForcedStatus.Passed);
  }
  return {
    'verification-template-version-stubs': vtvStubs,
    'fields-stubs': fieldsStubs,
    'vtv-to-selected-preset': vtvToPreset,
  };
};

export const SUPPORTED_FIELD_SCHEMAS = [
  FieldSchemaType.String,
  FieldSchemaType.Date,
  FieldSchemaType.Choices,
  FieldSchemaType.MultiChoices,
  FieldSchemaType.Boolean,
  FieldSchemaType.Number,
  FieldSchemaType.Integer,
  FieldSchemaType.Hash,
  FieldSchemaType.Json,
];

export const INITIAL_DATE_FIELD_VALUES: Record<string, string> = {
  birthdate: '1977-07-17',
};

export const INITIAL_STRING_FIELD_VALUES: Record<string, string> = {
  name_last: 'SAMPLE',
  name_first: 'ALEXANDER J',
  address_city: 'SAN FRANCISCO',
  address_street_1: '600 CALIFORNIA STREET',
  selected_id_class: 'dl',
  address_postal_code: '94109',
  address_subdivision: 'CA',
  address_country_code: 'US',
  identification_class: 'dl',
  identification_number: 'I1234562',
  selected_country_code: 'US',
};

const defaultValue = (fieldSchema: FieldSchema): FieldValue => {
  switch (fieldSchema.type) {
    case FieldSchemaType.String:
      return INITIAL_STRING_FIELD_VALUES[fieldSchema.key] || '';
    case FieldSchemaType.Date:
      return INITIAL_DATE_FIELD_VALUES[fieldSchema.key] || '';
    case FieldSchemaType.Choices:
      return INITIAL_STRING_FIELD_VALUES[fieldSchema.key] || null;
    case FieldSchemaType.MultiChoices:
      return [];
    case FieldSchemaType.Boolean:
      return true;
    case FieldSchemaType.Number:
    case FieldSchemaType.Integer:
      return null;
    default:
      return '';
  }
};

export const initialFieldsStubs = (fieldSchemas: FieldSchema[]): Record<string, FieldValue> => {
  return Object.fromEntries(
    fieldSchemas
      .filter((fieldSchema) => SUPPORTED_FIELD_SCHEMAS.includes(fieldSchema.type))
      .map((fieldSchema) => [fieldSchema.key, defaultValue(fieldSchema)]),
  );
};

export const vtmplvIdsTriggeredOnTransition = ({
  step,
  workflowVersionMap,
}: {
  step: InquiryTemplateVersionStep;
  workflowVersionMap: WorkflowVersionMap;
}): string[] => {
  return stepsWorkflowVersionTargets([step]).flatMap((wfvId) =>
    workflowVtmplvIds(workflowVersionMap?.[wfvId]?.attributes?.['steps-config'] ?? []),
  );
};

const INQUIRY_FIELD_REGEX = /trigger\.fields\.([A-Za-z0-9_]+$)/;

const getInquiryFieldFromPath = (path: string): string | undefined => {
  return path.match(INQUIRY_FIELD_REGEX)?.[1];
};

export const fieldsQueriedByConditionalOnTransition = ({
  step,
  workflowVersionMap,
}: {
  step: InquiryTemplateVersionStep;
  workflowVersionMap: WorkflowVersionMap;
}): string[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return stepsWorkflowVersionTargets([step]).flatMap((wfvId) => {
    const workflowVersion = workflowVersionMap?.[wfvId];
    if (workflowVersion == null) {
      return [];
    }

    const conditionalSteps = getAllWorkflowConditionalSteps(
      workflowVersion.attributes['steps-config'],
    );
    return conditionalSteps
      .flatMap((conditionalStep) => {
        return conditionalStep.conditionals;
      })
      .flatMap((conditional: WorkflowConditional | WorkflowElse) => {
        const rules = conditional.rules;
        if (rules == null || rules === true) {
          return [];
        }

        return rules.or
          .flatMap((or) => or.and)
          .flatMap((and) => {
            const parts = getConditionalRuleParts(and);

            const fields = [];

            if (parts.valueLeftConfig.type === RuleValueTypes.Field) {
              fields.push(
                ...parts.valueLeftConfig.paths
                  .map((path) => getInquiryFieldFromPath(path))
                  .filter(Boolean),
              );
            }
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            if (parts.valueRightConfig.type === RuleValueTypes.Field) {
              fields.push(
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ...parts.valueRightConfig.paths
                  .map((path) => getInquiryFieldFromPath(path))
                  .filter(Boolean),
              );
            }

            return fields;
          })
          .flat();
      });
  });
};

export const fieldsQueriedForVerificationCreationOnTransition = ({
  step,
  workflowVersionMap,
}: {
  step: InquiryTemplateVersionStep;
  workflowVersionMap: WorkflowVersionMap;
}): string[] => {
  return stepActionTargets({
    step,
    workflowVersionMap,
  }).flatMap((workflowAction) => {
    if (isCreateVerificationActionWithFieldMapping(workflowAction)) {
      return Object.values(workflowAction.params['verification-to-inquiry-field-mapping']);
    }

    return [];
  });
};

export const fieldsQueriedForAccountUpdateOnTransition = ({
  step,
  workflowVersionMap,
}: {
  step: InquiryTemplateVersionStep;
  workflowVersionMap: WorkflowVersionMap;
}): string[] => {
  return stepActionTargets({
    step,
    workflowVersionMap,
  }).flatMap((workflowAction) => {
    if (workflowAction.key === WorkflowStepActionKey.UpdateAccountFieldsFromInquiry) {
      return Object.values(
        workflowAction.params['account-to-inquiry-field-mapping'] as Record<string, string>,
      );
    }

    return [];
  });
};

export const fieldsSetFromUpdateFromVerificationsOnTransition = ({
  step,
  workflowVersionMap,
}: {
  step: InquiryTemplateVersionStep;
  workflowVersionMap: WorkflowVersionMap;
}): string[] => {
  return stepActionTargets({
    step,
    workflowVersionMap,
  }).flatMap((workflowAction) => {
    if ('inquiry-to-verification-field-mapping' in workflowAction.params) {
      // Because these are keys, the snakecase persisted keys are transformed to kebab case.
      // Transform them back to snake case.
      return Object.keys(
        workflowAction.params['inquiry-to-verification-field-mapping'] as Record<string, string>,
      ).map((key) => snakeCase(key));
    }
    return [];
  });
};

export const fieldsSetFromStepComponentsOnTransition = ({
  step,
}: {
  step: InquiryTemplateVersionStep;
}): string[] => {
  switch (step.type) {
    case UiStepType.Ui:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return uiStepAllComponentConfigs(step).flatMap((formFieldComponentConfig) => {
        if (isUiFormMultiFieldComponentConfig(formFieldComponentConfig)) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          return Object.entries((formFieldComponentConfig as UiComponentConfig).config)
            .map(([key, value]) => {
              return isComponentFieldConfigKey(key) ? value : null;
            })
            .filter(Boolean);
        } else if (isUiFormFieldComponentConfig(formFieldComponentConfig)) {
          return [formFieldComponentConfig.config.field];
        }
        return [];
      });
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
      return CombinedStepFieldKeyMap[step.type].map(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (configKey: keyof CombinedStepConfig) => step.config[configKey] as string,
      );
    default:
      return [];
  }
};
